/**
 * 「编辑听写任务」弹窗（2026-10-29 用户要求）。
 *
 * > 用户原话："听写任务要有编辑修改功能：(a) 可以改的就放开更改，不能改的就不能改。
 * >  (b) 你看一下哪些是可以改的，就放开更改。"
 *
 * ## 哪些能改、哪些不能改（这一页存在的全部理由）
 *
 * | 字段 | 能不能改 | 为什么 |
 * | --- | --- | --- |
 * | 标题 | ✅ | 纯展示 |
 * | 每次听几个 | ✅ | 只影响**以后新出的批次**（当前这一批的词已经定死） |
 * | 顺序（按课本 / 随机） | ✅ | 同上，只影响以后的批次 |
 * | 节奏（读几遍 / 间隔） | ✅ | 同上 —— 已生成的批次有自己的节奏快照 |
 * | **范围**（哪几本书 / 年级+学期 / 类别） | ⛔ **不能改** | 改了会让"已听过哪些词 / 总词数 / 还剩多少"全部错位：已提交的批次是按旧范围取的词，进度是用旧范围的总数算的。要换范围就**新建一个任务** |
 *
 * ## 两处刻意的实现选择
 *
 * 1. **打开时才去取完整任务**（`GET .../assignments/:id`）。听写历史的组头里只有
 *    摘要（标题 / 状态 / 进度），而编辑表单需要 `books`、`batchSize`、`shuffle`、`tempo`。
 *    让历史页去多取一份完整数据、只为了一个弹窗，不划算。
 * 2. **表单拆成内层组件**：数据到齐才挂载，`useState` 初始值直接来自服务端 ——
 *    不需要"先渲染空表单、再用 effect 填一遍"那种会闪的写法。
 *
 * ## 一个必须说清楚的后果
 *
 * 改「每次几个 / 顺序 / 节奏」**不会**改变已经生成的那一批（`picked_word_ids` 与
 * `tempo` 都是快照）。家长很容易以为"我改成 5 个，孩子手上那批就会变 5 个" ——
 * 所以弹窗里明写这句话，而不是让他改完发现没变化。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/Bits';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { toErrorMessage } from '../../lib/api/error';
import { semesterLabel } from '../../lib/api/dictation';
import * as assignmentApi from '../../lib/api/dictationAssignments';
import { TempoForm } from './DictationTempoForm';
import { describeTextbookLabel, gradeLabel, type DictationTempoConfig } from '@fht/shared';

export interface AssignmentEditModalProps {
  open: boolean;
  studentId: string;
  assignmentId: string;
  /** 列表里已有的标题（数据还没到齐时先显示在标题栏，避免闪一个空弹窗）。 */
  fallbackTitle: string;
  onClose: () => void;
}

export function AssignmentEditModal({
  open,
  studentId,
  assignmentId,
  fallbackTitle,
  onClose,
}: AssignmentEditModalProps) {
  const detailQuery = useQuery({
    queryKey: ['dictation-assignments', studentId, 'detail', assignmentId] as const,
    queryFn: () => assignmentApi.getAssignment(studentId, assignmentId),
    enabled: open && assignmentId !== '',
  });

  const detail = detailQuery.data ?? null;

  return (
    <Modal
      open={open}
      title="编辑这个任务"
      description={detail === null ? fallbackTitle : '只影响以后新出的批次；已经生成的那一批不会变。'}
      onClose={onClose}
      size="md"
      footer={null}
    >
      {detailQuery.isError ? (
        <ErrorState
          message={`没拿到这个任务：${toErrorMessage(detailQuery.error)}`}
          onRetry={() => void detailQuery.refetch()}
        />
      ) : detail === null ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      ) : (
        /* key 保证切任务时表单状态重建（不会残留上一个任务的值） */
        <AssignmentEditForm
          key={detail.id}
          studentId={studentId}
          assignment={detail}
          onDone={onClose}
        />
      )}
    </Modal>
  );
}

function AssignmentEditForm({
  studentId,
  assignment,
  onDone,
}: {
  studentId: string;
  assignment: assignmentApi.AssignmentView;
  onDone: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();

  const [title, setTitle] = useState(assignment.title);
  const [batchSize, setBatchSize] = useState(String(assignment.batchSize));
  const [shuffle, setShuffle] = useState(assignment.shuffle);
  const [tempo, setTempo] = useState<DictationTempoConfig>(assignment.tempo);
  const [error, setError] = useState<string | null>(null);

  const parsedBatchSize = (() => {
    const text = batchSize.trim();
    if (text === '') return null;
    const value = Number(text);
    return Number.isInteger(value) && value >= 1 && value <= 200 ? value : null;
  })();

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (parsedBatchSize === null) throw new Error('「每次听几个」要填 1~200 之间的整数。');
      if (title.trim() === '') throw new Error('任务名字不能是空的。');
      return assignmentApi.updateAssignment(studentId, assignment.id, {
        title: title.trim(),
        batchSize: parsedBatchSize,
        shuffle,
        tempo,
      });
    },
    onSuccess: async () => {
      toast.showToast('任务已保存', 'success');
      await qc.invalidateQueries({ queryKey: ['dictation-assignments', studentId] });
      await qc.invalidateQueries({ queryKey: ['dictation-sessions', studentId] });
      onDone();
    },
    onError: (err: unknown) => setError(toErrorMessage(err)),
  });

  /** 范围（只读）：几本书，或 年级 + 学期。 */
  const scopeText = (() => {
    const books = assignment.books ?? [];
    if (books.length > 0) {
      return books.length === 1 && books[0] !== undefined
        ? describeTextbookLabel(books[0])
        : `${books.map((book) => book.name).join('、')}（共 ${books.length} 本）`;
    }
    if (assignment.gradeCode !== null) {
      const semester = assignment.semester === null ? '' : semesterLabel(assignment.semester);
      return `${gradeLabel(assignment.gradeCode)}${semester}`;
    }
    return '范围没拿到';
  })();

  return (
    <div className="space-y-4">
      {/* ---------- 不能改的部分：说清楚为什么 ---------- */}
      <div className="rounded-xl bg-slate-50 px-3 py-2">
        <p className="text-xs font-medium text-slate-500">范围（不能改）</p>
        <p className="mt-0.5 text-sm text-slate-700">{scopeText}</p>
        <p className="mt-1 text-[11px] text-slate-500">
          范围决定"哪些词算这个任务"，改了会让已听进度与剩余词数对不上。
          要换书或换年级，请用「批量任务」新建一个任务。
        </p>
      </div>

      {/* ---------- 能改的 ---------- */}
      <div>
        <label className="block text-sm font-medium text-slate-700" htmlFor="assignment-edit-title">
          任务名字
        </label>
        <input
          id="assignment-edit-title"
          className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          value={title}
          maxLength={64}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700" htmlFor="assignment-edit-size">
            每次听写几个
          </label>
          <input
            id="assignment-edit-size"
            className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            inputMode="numeric"
            value={batchSize}
            onChange={(event) => setBatchSize(event.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            1~200。最后一批剩几个就听几个。
            {parsedBatchSize === null && batchSize.trim() !== '' && (
              <span className="ml-1 text-rose-600">这个数不合法。</span>
            )}
          </p>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">顺序</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {(
              [
                { value: false, label: '按课本顺序' },
                { value: true, label: '每次随机' },
              ] as const
            ).map((option) => (
              <button
                key={String(option.value)}
                type="button"
                aria-pressed={shuffle === option.value}
                onClick={() => setShuffle(option.value)}
                className={
                  shuffle === option.value
                    ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                    : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                }
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {shuffle ? '每批从还没听过的词里随机取。' : '跟着课本一行一行往下听。'}
          </p>
        </div>
      </div>

      <div>
        <p className="mb-1 text-sm font-medium text-slate-700">听写节奏</p>
        <TempoForm
          tempo={tempo}
          wordCount={parsedBatchSize}
          disabled={saveMutation.isPending}
          onChange={(patch) => setTempo((prev) => ({ ...prev, ...patch }))}
        />
      </div>

      <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
        已经生成的那一批（比如孩子手上正在做的、或已经交过的）**不会**因为这次修改而改变：
        它的词和节奏在生成时就定下来了。
      </p>

      {error !== null && (
        <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="neutral">当前状态：{assignmentApi.assignmentStatusLabel(assignment.status)}</Badge>
        <Badge tone="neutral">已交 {assignment.progress.submittedBatches ?? '—'} 批</Badge>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" disabled={saveMutation.isPending} onClick={onDone}>
          取消
        </Button>
        <Button
          loading={saveMutation.isPending}
          onClick={() => {
            setError(null);
            saveMutation.mutate();
          }}
        >
          保存
        </Button>
      </div>
    </div>
  );
}
